import React from 'react';
import { useTranslation } from 'react-i18next';
import Modal from '../ui/Modal';
import { helper } from '../../utils/helper';

/**
 * Shape of the `pendingInvoice` object inside the 409 `pending_payment`
 * response from `POST /change-plan`. The OpenAPI spec inlines this object
 * (anonymous schema), so it's not generated as a typed model - narrowed
 * locally based on the documented contract.
 */
export interface PendingInvoice {
  id: string;
  hostedInvoiceUrl: string | null;
  amountRemaining: number;
  status: string;
}

interface LicensePendingPaymentModalProps {
  isOpen: boolean;
  onClose: () => void;
  pendingInvoice: PendingInvoice;
  /**
   * Called when the user clicks "Complete payment" - the parent owns the
   * popup window and polling so the URL is just forwarded back here.
   */
  onPay: (hostedInvoiceUrl: string) => void;
}

const LicensePendingPaymentModal: React.FC<LicensePendingPaymentModalProps> = ({
  isOpen,
  onClose,
  pendingInvoice,
  onPay,
}) => {
  const { t } = useTranslation();

  const formattedAmount = helper.formatPrice({
    amount: pendingInvoice.amountRemaining,
    currency: 'EUR',
  });

  const hasUrl = !!pendingInvoice.hostedInvoiceUrl;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('subscription_pending_payment_title')}
      hideCloseButton={true}
      isCentered={true}
    >
      <div className="modal-success">
        <div className="modal-success-text">
          {hasUrl
            ? t('subscription_pending_payment_body', { amount: formattedAmount })
            : t('subscription_pending_payment_no_url_text', { id: pendingInvoice.id })}
        </div>
        {hasUrl && (
          <div className="modal-pending-payment-invoice-id">
            {t('subscription_pending_payment_invoice_id', { id: pendingInvoice.id })}
          </div>
        )}
        <div className="modal-buttons">
          <button
            className="modal-button cancel"
            onClick={onClose}
          >
            {t('subscription_pending_payment_close_button')}
          </button>
          {hasUrl && pendingInvoice.hostedInvoiceUrl && (
            <button
              className="modal-button confirm"
              onClick={() => onPay(pendingInvoice.hostedInvoiceUrl as string)}
            >
              {t('subscription_pending_payment_pay_button')}
            </button>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default LicensePendingPaymentModal;
